import DashboardLayout from "../../lib/components/layouts/dashboard";
import { AppContext } from "lib/contexts/app-context";
import { useContext, useEffect, useState } from "react";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { isPatientUser } from "lib/utils/util";
import usePatients from "lib/hooks/usePatients";
import PatientCalendar from "../../lib/components/patient/patient-calender";
import moment from "moment";
import { momentLocalizer } from "react-big-calendar";
import SimpleCard from "lib/components/common/simple-card";
import { utcToLocalClock } from "lib/utils/date-utils";
import SimpleModal from "lib/components/common/simple-modal";
import PatientBookingSummaryTile from "lib/components/common/patient/patient-booking-summary-tile";
import PatientSessionBookingSummaryTile from "lib/components/common/patient/patient-session-booking-summary-tile";
import { toast } from "sonner";

const localizer = momentLocalizer(moment);

const PatientCalenderPage = () => {
  const { appState } = useContext(AppContext);
  const patientId = appState?.user_id;
  const { getBookingsDetails, getSessionBookingsDetails, changeSessionBookingStatus, loading: patientsLoading } = usePatients();

  const [bookingsDetails, setBookingsDetails] = useState<
    PatientAppointmentBookingDetails[]
  >([]);
  const [sessionBookingsDetails, setSessionBookingsDetails] = useState<
    SessionBookingDetails[]
  >([]);

  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);

  const [selectedAppointment, setSelectedAppointment] =
    useState<PatientAppointmentBookingDetails | null>(null);

  const [selectedSession, setSelectedSession] =
    useState<PatientSessionBookingDetails | null>(null);

  const BreadcrumbItems = [
    {
      label: "Dashboard",
      path: isPatientUser(appState?.userRole)
        ? PAGE_ROUTES.PATIENT.Dashboard
        : PAGE_ROUTES.PRACTITIONER.Dashboard,
    },
    {
      label: "Calender",
      path: PAGE_ROUTES.PATIENT.Calender,
    },
  ];

  useEffect(() => {
    if (!patientId) return;
    getBookingsDetails(patientId, (bookings) => {
      setBookingsDetails(bookings);
    });
    getSessionBookingsDetails(patientId, (bookingsessions) => {
      setSessionBookingsDetails(bookingsessions);
    });
  }, [patientId]);

  const handleCalendarEventClick = (event) => {

    if (event.resource.type === "APPOINTMENT") {
      setSelectedAppointment(event.resource);
      setShowAppointmentModal(true);
    }

    if (event.resource.type === "SESSION_BOOKING") {
      setSelectedSession(event.resource);
      setShowSessionModal(true);
    }
  };

  // Handler to update session booking status using API
  const onChangeSessionBookingStatus = async (sessionId: string, newStatus: string) => {
    try {
      await changeSessionBookingStatus(appState.user_id, sessionId, newStatus);
      setSessionBookingsDetails((prev) =>
        prev.map((session) =>
          session.id === sessionId ? { ...session, status: newStatus } : session
        )
      );
      setShowSessionModal(false);
      toast.success(`Session status updated to '${newStatus}'.`);
    } catch (error) {
      toast.error('Failed to update session status. Please try again.');
    }
  };

  return (
    <DashboardLayout breadcrumbItems={BreadcrumbItems}>
      <SimpleCard
        title="Your Calender"
      >
        <PatientCalendar
          localizer={localizer}
          showAppointments={false}
          showSessionBookings={false}
          appointmentList={bookingsDetails.map(b => ({
            title: b?.therapyName ? `${b.therapyName}` : "Appointment",
            start: new Date(b.fromTime),
            end: new Date(new Date(b.fromTime).getTime() + b.duration * 60000),
            resource: {
              ...b,
              type: "APPOINTMENT",
            },
          }))}
          
          sessionBookingList={sessionBookingsDetails.map(s => ({
            title: s?.therapyName ? `${s.therapyName}` : "Session Booking",
            start: utcToLocalClock(s.fromTime),
            end: new Date(utcToLocalClock(s.fromTime).getTime() + s.duration * 60000),
            resource: {
              ...s,
              type: "SESSION_BOOKING",
            },
          }))}
          onEventClick={handleCalendarEventClick}
        />
      </SimpleCard>
      <SimpleModal
        open={showAppointmentModal}
        close={() => setShowAppointmentModal(false)}
        title="Booking Details"
        size="sm"
      >
        {selectedAppointment && (
          <PatientBookingSummaryTile
            fromTime={selectedAppointment.fromTime}
            {...selectedAppointment}
          />
        )}
      </SimpleModal>

      <SimpleModal
        open={showSessionModal}
        close={() => setShowSessionModal(false)}
        title="Session Details"
        size="sm"
      >
        {selectedSession && (
          <PatientSessionBookingSummaryTile
            fromTime={selectedSession.fromTime}
            {...selectedSession}
            onChangeSessionBookingStatus={onChangeSessionBookingStatus}
          />
        )}
      </SimpleModal>
    </DashboardLayout>
  );
};

export default PatientCalenderPage;
